import notificationSoundFile from '@/assets/sounds/notification.wav'

// key เก็บสถานะเปิด/ปิดเสียงใน localStorage (share ข้าม tab ในเครื่องเดียวกัน)
const STORAGE_KEY = 'gochat_notification_sound_enabled'
// กันเสียงรัว: เล่นได้ครั้งเดียวต่อ 2 วินาที เมื่อข้อความเข้าเป็นชุด (WS burst/retry)
const DEBOUNCE_MS = 2000

class NotificationSound {
  constructor() {
    this._audio = null
    this._lastPlayedAt = 0
    this._userInteracted = false

    // Chrome autoplay policy: ต้องมี user interaction (คลิก/กดคีย์) ก่อน จึงจะเล่นเสียงได้
    // ดักครั้งแรกครั้งเดียวเพื่อ "ปลดล็อก" การเล่นเสียง แล้วถอด listener ทิ้ง
    this._unlockHandler = () => {
      this._userInteracted = true
      // ไม่สร้าง/โหลด wav ตรงนี้ — ให้สร้างตอน play()/toggle() เท่านั้น
      // (default ปิด → ไม่โหลดไฟล์เสียงทิ้งให้ทุก user ที่ไม่ได้เปิดเสียง)
      document.removeEventListener('click', this._unlockHandler)
      document.removeEventListener('keydown', this._unlockHandler)
    }
    document.addEventListener('click', this._unlockHandler)
    document.addEventListener('keydown', this._unlockHandler)
  }

  // สร้าง Audio instance เดียวใช้ตลอด app lifetime (ไม่สร้างซ้ำทุกครั้งที่เล่น)
  _ensureAudio() {
    if (!this._audio) {
      this._audio = new Audio(notificationSoundFile)
      this._audio.volume = 0.6
      this._audio.preload = 'auto'
    }
    return this._audio
  }

  // ตรวจว่า user เปิดเสียงไว้หรือไม่ — default ปิด (null = ยังไม่เคยตั้งค่า)
  // ครอบ try/catch ทั้งก้อน กันเคส localStorage เข้าถึงไม่ได้ (private mode/sandboxed iframe/นโยบายบล็อก)
  // ไม่ให้ exception หลุดขึ้นไปที่ instantMessageEvent (hot path ของทุกข้อความเข้า)
  isEnabled() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored === null) return false
      return JSON.parse(stored)
    } catch (e) {
      return false
    }
  }

  // สลับเปิด/ปิดเสียง แล้ว return สถานะใหม่ (persist ลง localStorage)
  // toggle() ถูกเรียกจาก user gesture (คลิก) เสมอ — ใช้จังหวะนี้ unlock Audio ให้ Safari/WebKit ด้วย
  toggle() {
    const newState = !this.isEnabled()
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState))
    } catch (e) {
      // เขียน localStorage ไม่ได้ — ยังสลับสถานะใน UI ต่อได้ ไม่ให้ throw ทะลุขึ้น
    }
    if (newState) {
      // Safari/WebKit: play() ตัว element ต้องเกิดใน user gesture นี้เพื่อ unlock
      // ไม่งั้น play() จาก WS handler ทีหลังจะโดน NotAllowedError แล้วเงียบสนิท (Chrome ผ่านด้วย sticky activation)
      // เล่นสั้นๆ แล้วหยุด = ทั้ง unlock + เป็นเสียงยืนยันตอนกดเปิด
      const audio = this._ensureAudio()
      try {
        const p = audio.play()
        if (p && typeof p.then === 'function') {
          p.then(() => { /* ปล่อยให้ดังจบเป็นเสียงยืนยัน */ }).catch(() => {})
        }
      } catch (e) {}
    }
    // แจ้ง component อื่น (bell บนแถบบน / modal Settings) ให้ sync ไอคอน/สถานะแบบ same-tab
    this._emitChange(newState)
    return newState
  }

  // ยิง custom event ให้ทุก component ที่แสดงสถานะเสียง sync ตรงกัน (bell บนแถบบน + Settings modal)
  _emitChange(state) {
    try {
      window.dispatchEvent(new CustomEvent('gochat:notification-sound-changed', { detail: state }))
    } catch (e) {}
  }

  // ให้ component subscribe การเปลี่ยนสถานะ (คืน unsubscribe function) — bell บนแถบบน + Settings modal
  onChange(handler) {
    const fn = (e) => handler(e && e.detail)
    window.addEventListener('gochat:notification-sound-changed', fn)
    return () => window.removeEventListener('gochat:notification-sound-changed', fn)
  }

  // เล่นเสียง — เงื่อนไข: เปิดเสียงไว้ + ผ่าน user interaction แล้ว + ไม่ติด debounce
  play() {
    // ครอบ try/catch ทั้งก้อน: new Audio()/currentTime อาจ throw ได้ (บาง browser/สถานะ)
    // hot path ของ WS handler — เสียงพังห้ามทำให้ instantMessageEvent ล้ม/ข้อความหาย
    try {
      if (!this.isEnabled()) return
      if (!this._userInteracted) return
      const now = Date.now()
      if (now - this._lastPlayedAt < DEBOUNCE_MS) return
      this._lastPlayedAt = now
      const audio = this._ensureAudio()
      audio.currentTime = 0
      // กลืน error กรณี browser ยัง block
      audio.play().catch(() => {})
    } catch (e) {
      // เงียบไว้ — กัน exception หลุดขึ้น WS handler
    }
  }
}

// singleton — import ที่ไหนก็ได้ instance เดียวกัน
export default new NotificationSound()



// WEBPACK FOOTER //
// ./src/services/notificationSound.js